// Shader patch of the map surface material: baked light from the volume of bakedLight.ts.
//
// Per fragment the patch samples the bake at `position + normal * offset` and
//   - scales the ambient light (hemisphere + environment diffuse) by the local occlusion, and the sky share
//     of the hemisphere light by the sky visibility,
//   - adds the sun's bounce light and the baked point lights as indirect diffuse,
//   - scales the environment specular by a specular occlusion derived from the occlusion,
//   - darkens and warms crevices (dirt), lifts convex edges and roughens crevices, scaled by the material's
//     grime factor.
// Direct sun and point lights are untouched (sun shadows come from the shadow map). All materials share one
// program: the only per-material input is the `uBakeGrime` uniform. With `uBakeParams.x = 0` the patched
// shader reduces to plain hemisphere lighting.

import * as THREE from "three";
import { CHROMA_MAX, SKY_B_RANGE } from "../bakedFormat";

export const PROGRAM_KEY = "bakedSurface-v1";

/** Beyond the volume the baked light fades to nothing over 1 / FADE_PER_EXTENT of its extent (about 3 m on a 120 m map). */
const FADE_PER_EXTENT = 40;

/** Uniforms shared by every patched material (the objects are referenced, not copied). */
export interface BakeUniforms {
  uBakeOcc: THREE.IUniform<THREE.Data3DTexture>;
  uBakeSky: THREE.IUniform<THREE.Data3DTexture>;
  uBakeBounce: THREE.IUniform<THREE.Data3DTexture>;
  uBakeTint: THREE.IUniform<THREE.Data3DTexture>;
  /** The two unit-luminance colours the lamp colour position runs between. */
  uBakeLampTints: THREE.IUniform<THREE.Vector3[]>;
  /** Volume minimum corner (m). */
  uBakeOrigin: THREE.IUniform<THREE.Vector3>;
  /** 1 / extent of the volume per axis. */
  uBakeInvSize: THREE.IUniform<THREE.Vector3>;
  /** x: bounce scale, y: lamp scale. */
  uBakeScale: THREE.IUniform<THREE.Vector2>;
  /** Sun colour times intensity (linear): what the bounce is relative to. */
  uBakeSun: THREE.IUniform<THREE.Vector3>;
  /** x: level (0 off, 1 occlusion + sky, 2 + bounce + lamps), y: sampling offset along the normal (m), z: exponent of the baked occlusion, w: exponent of the sky visibility (0 = ignored, 1 = as baked). */
  uBakeParams: THREE.IUniform<THREE.Vector4>;
  /** x: bounce strength, y: lamp strength, z: debug view (0 off, 1 occlusion, 2 sky, 3 bounce, 4 lamps), w: material response strength (dirt, edge wear, roughness). */
  uBakeMix: THREE.IUniform<THREE.Vector4>;
}

function glsl(n: number): string {
  return Number.isInteger(n) ? n.toFixed(1) : String(n);
}

/** Inserts `extra` before or after the first `token`; throws when three's shader no longer has it. */
export function inject(source: string, token: string, extra: string, where: "before" | "after"): string {
  const at = source.indexOf(token);
  if (at < 0) throw new Error(`surface shader: "${token}" is missing from the base shader`);
  const cut = where === "before" ? at : at + token.length;
  return `${source.slice(0, cut)}\n${extra}\n${source.slice(cut)}`;
}

export const VERTEX_PARS = "varying vec3 vBakePos;\nvarying vec3 vBakeNormal;";
export const VERTEX_MAIN = `vBakePos = ( modelMatrix * vec4( transformed, 1.0 ) ).xyz;
vBakeNormal = mat3( modelMatrix ) * objectNormal;`;

export const FRAGMENT_PARS = `varying vec3 vBakePos;
varying vec3 vBakeNormal;
uniform sampler3D uBakeOcc;
uniform sampler3D uBakeSky;
uniform sampler3D uBakeBounce;
uniform sampler3D uBakeTint;
uniform vec3 uBakeLampTints[ 2 ];
uniform vec3 uBakeOrigin;
uniform vec3 uBakeInvSize;
uniform vec2 uBakeScale;
uniform vec3 uBakeSun;
uniform vec4 uBakeParams;
uniform vec4 uBakeMix;
uniform float uBakeGrime;

vec3 bakeChroma( float cr, float cb ) {
	vec3 c = vec3( cr, 0.0, cb ) * ${glsl(CHROMA_MAX)};
	c.g = max( 1.0 - 0.2126 * c.r - 0.0722 * c.b, 0.0 ) / 0.7152;
	return c;
}`;

/** Samples the bake and applies the material response; runs before the roughness is read. */
export const FRAGMENT_SAMPLE = `
vec3 bakeN = normalize( vBakeNormal ) * ( gl_FrontFacing ? 1.0 : - 1.0 );
float bakeAo = 1.0;
float bakeOpen = 0.5;
float bakeSky = 1.0;
vec3 bakeBounce = vec3( 0.0 );
vec3 bakeLamp = vec3( 0.0 );
if ( uBakeParams.x > 0.5 ) {
	vec3 bakeUvw = ( vBakePos + bakeN * uBakeParams.y - uBakeOrigin ) * uBakeInvSize;
	vec4 bakeOcc = texture( uBakeOcc, bakeUvw );
	bakeAo = clamp( bakeOcc.x + dot( bakeOcc.yzw - 0.5, bakeN ), 0.0, 1.0 );
	bakeOpen = bakeOcc.x;
	vec4 bakeS = texture( uBakeSky, bakeUvw );
	bakeSky = clamp( bakeS.x + ${glsl(SKY_B_RANGE)} * dot( bakeS.yzw - 0.5, bakeN ), 0.0, 1.0 );
	if ( uBakeParams.x > 1.5 ) {
		vec4 bakeB = texture( uBakeBounce, bakeUvw );
		float bakeY = max( bakeB.x + dot( bakeB.yzw - 0.5, bakeN ), 0.0 ) * uBakeScale.x;
		vec4 bakeT = texture( uBakeTint, bakeUvw );
		bakeBounce = bakeY * bakeChroma( bakeT.x, bakeT.y );
		bakeLamp = bakeT.z * bakeT.z * uBakeScale.y * mix( uBakeLampTints[ 0 ], uBakeLampTints[ 1 ], bakeT.w );
	}
	vec3 bakeOut = max( abs( bakeUvw - 0.5 ) - 0.5, 0.0 );
	float bakeFade = 1.0 - clamp( max( bakeOut.x, max( bakeOut.y, bakeOut.z ) ) * ${glsl(FADE_PER_EXTENT)}, 0.0, 1.0 );
	bakeAo = mix( 1.0, bakeAo, bakeFade );
	bakeOpen = mix( 0.5, bakeOpen, bakeFade );
	bakeSky = mix( 1.0, bakeSky, bakeFade );
	bakeBounce *= bakeFade;
	bakeLamp *= bakeFade;
	float bakeCrevice = 1.0 - bakeAo;
	float bakeEdge = clamp( ( bakeOpen - 0.7 ) * 4.0, 0.0, 1.0 );
	float bakeGrime = uBakeGrime * uBakeMix.w;
	diffuseColor.rgb *= mix( vec3( 1.0 ), vec3( 0.7, 0.6, 0.5 ), clamp( bakeCrevice * 1.2, 0.0, 1.0 ) * bakeGrime );
	diffuseColor.rgb *= 1.0 + bakeEdge * bakeGrime * 0.2;
}`;

/** Runs right after the roughness is read. */
export const FRAGMENT_ROUGHNESS = `
if ( uBakeParams.x > 0.5 ) {
	float bakeWorn = uBakeMix.w * ( 0.2 + 0.6 * uBakeGrime );
	roughnessFactor = clamp( roughnessFactor * ( 1.0 + ( 1.0 - bakeAo ) * bakeWorn ) * mix( 1.0, 0.88, clamp( ( bakeAo - 0.85 ) * 6.0, 0.0, 1.0 ) * uBakeMix.w ), 0.04, 1.0 );
}`;

/** Modulates the indirect light before three accumulates it. */
export const FRAGMENT_INDIRECT = `
if ( uBakeParams.x > 0.5 ) {
	float bakeAoMul = pow( bakeAo, uBakeParams.z );
	float bakeSkyMul = pow( bakeSky, uBakeParams.w );
	float bakeHemiW = 0.5 + 0.5 * bakeN.y;
	#if defined( RE_IndirectDiffuse )
		#if NUM_HEMI_LIGHTS > 0
			irradiance -= hemisphereLights[ 0 ].skyColor * ( bakeHemiW * ( 1.0 - bakeSkyMul ) );
		#endif
		irradiance = irradiance * bakeAoMul + bakeBounce * uBakeSun * uBakeMix.x + bakeLamp * uBakeMix.y;
		iblIrradiance *= bakeAoMul * mix( 1.0, bakeSkyMul, bakeHemiW );
	#endif
	#if defined( RE_IndirectSpecular )
		radiance *= computeSpecularOcclusion( saturate( dot( geometryNormal, geometryViewDir ) ), bakeAoMul * mix( 1.0, bakeSkyMul, bakeHemiW ), material.roughness );
	#endif
}`;

/** Debug views replace the final colour (already display encoded: values are shown as sRGB). */
export const FRAGMENT_DEBUG = `
if ( uBakeMix.z > 0.5 ) {
	vec3 bakeShow = vec3( pow( bakeAo, uBakeParams.z ) );
	if ( uBakeMix.z > 3.5 ) bakeShow = bakeLamp * 0.5;
	else if ( uBakeMix.z > 2.5 ) bakeShow = bakeBounce * 3.0;
	else if ( uBakeMix.z > 1.5 ) bakeShow = vec3( bakeSky );
	gl_FragColor = vec4( pow( bakeShow, vec3( 1.0 / 2.2 ) ), 1.0 );
}`;

/**
 * Patches a MeshStandardMaterial that draws static map geometry (world-space vertices, identity or rigid
 * model matrix) with the baked-light shader. `grime` (0..1) scales dirt, edge wear and roughness response.
 */
export function applyBakedSurface(material: THREE.MeshStandardMaterial, uniforms: BakeUniforms, grime: number): void {
  const grimeUniform = { value: grime };
  material.onBeforeCompile = (shader) => {
    Object.assign(shader.uniforms, uniforms, { uBakeGrime: grimeUniform });
    let vs = inject(shader.vertexShader, "#include <common>", VERTEX_PARS, "after");
    vs = inject(vs, "#include <project_vertex>", VERTEX_MAIN, "after");
    shader.vertexShader = vs;
    let fs = inject(shader.fragmentShader, "#include <common>", FRAGMENT_PARS, "after");
    fs = inject(fs, "#include <roughnessmap_fragment>", FRAGMENT_SAMPLE, "before");
    fs = inject(fs, "#include <roughnessmap_fragment>", FRAGMENT_ROUGHNESS, "after");
    fs = inject(fs, "#include <lights_fragment_end>", FRAGMENT_INDIRECT, "before");
    fs = inject(fs, "#include <dithering_fragment>", FRAGMENT_DEBUG, "after");
    shader.fragmentShader = fs;
  };
  material.customProgramCacheKey = () => PROGRAM_KEY;
}
